import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { AppBar, Box, Grid, IconButton, Paper, Tab, TabTypeMap, Tabs, styled } from "@mui/material";
import React, { Dispatch, SetStateAction, useMemo } from "react";

import { DynamicContentTab, DynamicContentView } from "../../Types/DynamicContentView";
import DynamicContentPanelTab from "./DynamicContentPanelTab";

interface Props {
  dynamicContentView?: DynamicContentView;
  setDynamicContentView: Dispatch<SetStateAction<DynamicContentView | undefined>>;
  toggleMaximize?: () => void;
}

const DynamicContentPanel: React.FC<Props> = ({ dynamicContentView, setDynamicContentView, toggleMaximize }: Props) => {
  const setDynamicContentTab = (index: number) => (dynamicContentTab: DynamicContentTab) =>
    setDynamicContentView(oldView => {
      if (!oldView) return oldView;
      const tabs = [...oldView.tabs];
      tabs[index] = dynamicContentTab;
      return { ...oldView, tabs };
    });
  const dcTabs = useMemo(
    () =>
      dynamicContentView?.tabs.map((dynamicContentTab, index) => (
        <Box sx={{ display: dynamicContentView.tabIndex === index ? undefined : "none", width: "100%" }} key={index}>
          <DynamicContentPanelTab
            dynamicContentTab={dynamicContentTab}
            setDynamicContentTab={setDynamicContentTab(index)}
          />
        </Box>
      )) || [],
    [dynamicContentView]
  );

  return (
    <Paper sx={{ height: "100%", overflow: "auto" }}>
      <AppBar position="sticky" color="default">
        <Grid container>
          <Grid item xs zeroMinWidth>
            <Tabs
              value={dynamicContentView?.tabIndex ?? false}
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_e, tabIndex) =>
                setDynamicContentView(oldView => (oldView ? { ...oldView, tabIndex } : oldView))
              }
            >
              {dynamicContentView?.tabs.map((dc, i) => (
                <DynamicContentMuiTab label={dc.title} key={i} />
              ))}
            </Tabs>
          </Grid>
          {toggleMaximize && (
            <Grid item>
              <IconButton edge="end" color="inherit" onClick={toggleMaximize} style={{ marginRight: "4px" }}>
                <AspectRatioIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </AppBar>
      <Box display="flex" flexGrow={1} sx={{ mt: 1 }}>
        {dcTabs}
      </Box>
    </Paper>
  );
};

const DynamicContentMuiTab = styled(Tab)<Partial<TabTypeMap>>(() => ({
  padding: "4px 8px",
  minWidth: "unset",
  minHeight: "40px"
}));

export default DynamicContentPanel;
